(() => {
  const storageKey = 'm-compare-products';
  const widget = document.getElementById('plovara-compare-floating-widget');
  const modal = document.getElementById('PlovaraCompareModal');

  const getHandles = () => {
    try {
      return [...new Set(JSON.parse(localStorage.getItem(storageKey)) || [])];
    } catch (error) {
      return [];
    }
  };

  const saveHandles = (handles) => {
    localStorage.setItem(storageKey, JSON.stringify(handles));
    if (window.MinimogTheme && window.MinimogTheme.CompareProduct) {
      window.MinimogTheme.CompareProduct.products = [...handles];
    }
  };

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>'"]/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
    }[char]));

  const formatPrice = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
    }).format((Number(cents) || 0) / 100);

  const cleanDescription = (html = '') => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // remove styles/scripts
    tmp.querySelectorAll('script, style').forEach(el => el.remove());
    const text = tmp.textContent.trim().replace(/\s+/g, ' ');
    if (text.length > 210) {
      return text.substring(0, 205).trim() + '…';
    }
    return text || 'No description available.';
  };

  // Sync compare buttons and floating widget count
  const syncButtonsAndWidget = (handles) => {
    // Card compare buttons
    document.querySelectorAll('.m-compare-button').forEach((button) => {
      const handle = button.dataset.productHandle;
      const isAdded = handles.includes(handle);
      button.classList.toggle('added-to-compare', isAdded);
      button.setAttribute('aria-label', isAdded ? 'Remove from compare' : 'Add to compare');
      button.setAttribute('title', isAdded ? 'Remove from compare' : 'Add to compare');
    });

    // Header compare count badges (if any exist)
    document.querySelectorAll('.m-compare-count').forEach((count) => {
      count.textContent = handles.length;
      count.classList.toggle('m:hidden', handles.length === 0);
    });

    // Floating Widget
    if (widget) {
      const badge = widget.querySelector('[data-compare-widget-count]');
      if (badge) badge.textContent = handles.length;

      if (handles.length > 0) {
        widget.style.display = 'inline-flex';
        requestAnimationFrame(() => {
          widget.classList.add('is-visible');
        });
      } else {
        widget.classList.remove('is-visible');
        setTimeout(() => {
          if (!getHandles().length) {
            widget.style.display = 'none';
          }
        }, 300);
      }
    }
  };

  // Render modal content
  const renderModal = async () => {
    if (!modal) return;

    const modalGrid = modal.querySelector('[data-compare-modal-grid]');
    const modalEmpty = modal.querySelector('[data-compare-modal-empty]');
    const modalLoading = modal.querySelector('[data-compare-modal-loading]');
    const modalCount = modal.querySelector('[data-compare-modal-count]');

    const handles = getHandles();
    if (modalCount) {
      modalCount.textContent = `${handles.length} product${handles.length === 1 ? '' : 's'} selected`;
    }

    syncButtonsAndWidget(handles);

    if (!handles.length) {
      if (modalGrid) modalGrid.innerHTML = '';
      if (modalLoading) modalLoading.hidden = true;
      if (modalEmpty) modalEmpty.hidden = false;
      return;
    }

    if (modalEmpty) modalEmpty.hidden = true;
    if (modalLoading) modalLoading.hidden = false;
    if (modalGrid) modalGrid.innerHTML = '';

    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
          return res.ok ? await res.json() : null;
        } catch (error) {
          return null;
        }
      })
    );

    if (modalLoading) modalLoading.hidden = true;

    const validProducts = products.filter(Boolean);
    if (!validProducts.length) {
      if (modalEmpty) modalEmpty.hidden = false;
      return;
    }

    if (modalGrid) {
      modalGrid.innerHTML = validProducts
        .map((product) => {
          const image = product.featured_image || (product.images && product.images[0]) || '';
          const handle = product.handle || '';
          const hasDiscount = product.compare_at_price && product.compare_at_price > product.price;
          const discountPct = hasDiscount
            ? Math.round(((product.compare_at_price - product.price) * 100) / product.compare_at_price)
            : 0;
          const desc = cleanDescription(product.description || '');

          return `
            <article class="pl-compare-card" data-compare-handle="${escapeHtml(handle)}">
              <div class="pl-compare-card__image-box">
                <a href="${escapeHtml(product.url || `/products/${handle}`)}" class="pl-compare-card__image-link">
                  ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : '<div class="pl-compare-card__no-img">No Image</div>'}
                </a>
                <button type="button" class="pl-compare-card__remove-btn" data-remove-compare="${escapeHtml(handle)}" aria-label="Remove ${escapeHtml(product.title)}">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                </button>
              </div>

              <div class="pl-compare-card__info">
                <h4 class="pl-compare-card__title">
                  <a href="${escapeHtml(product.url || `/products/${handle}`)}">${escapeHtml(product.title)}</a>
                </h4>

                <div class="pl-compare-card__price-row">
                  <span class="pl-compare-card__price">${formatPrice(product.price)}</span>
                  ${hasDiscount ? `<span class="pl-compare-card__compare-price">${formatPrice(product.compare_at_price)}</span>` : ''}
                  ${hasDiscount ? `<span class="pl-compare-card__discount-pill">-${discountPct}%</span>` : ''}
                </div>

                <div class="pl-compare-card__specs">
                  <div class="pl-compare-spec-row">
                    <span class="pl-compare-spec-label">Availability:</span>
                    <span class="pl-compare-spec-val ${product.available ? 'is-instock' : 'is-outstock'}">
                      ${product.available ? '✓ In Stock' : '✕ Sold Out'}
                    </span>
                  </div>
                  ${product.vendor ? `
                    <div class="pl-compare-spec-row">
                      <span class="pl-compare-spec-label">Brand:</span>
                      <span class="pl-compare-spec-val">${escapeHtml(product.vendor)}</span>
                    </div>
                  ` : ''}
                </div>

                <div class="pl-compare-card__desc">
                  <span class="pl-compare-desc-label">Description:</span>
                  <p>${escapeHtml(desc)}</p>
                </div>

                <div class="pl-compare-card__actions">
                  <a href="${escapeHtml(product.url || `/products/${handle}`)}" class="pl-compare-card__btn">
                    ${product.available ? 'View Product' : 'Sold Out'}
                  </a>
                </div>
              </div>
            </article>
          `;
        })
        .join('');
    }
  };

  const openModal = () => {
    if (!modal) return;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('prevent-scroll');
    renderModal();
  };

  const closeModal = () => {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('prevent-scroll');
  };

  // Product card compare button click listener
  document.addEventListener('click', (event) => {
    const compareButton = event.target.closest('.m-compare-button');
    if (!compareButton) return;

    event.preventDefault();
    event.stopPropagation();

    const handle = compareButton.dataset.productHandle;
    if (!handle) return;

    let handles = getHandles();
    if (handles.includes(handle)) {
      // Toggle off / remove
      handles = handles.filter((h) => h !== handle);
    } else {
      // Add to compare
      handles.push(handle);
    }

    saveHandles(handles);
    syncButtonsAndWidget(handles);

    // Bump animation on widget to notify user
    if (widget && handles.length > 0) {
      widget.classList.remove('pl-widget-bump');
      void widget.offsetWidth; // force reflow
      widget.classList.add('pl-widget-bump');
    }

    // If modal is open while toggling, re-render
    if (modal && modal.classList.contains('is-open')) {
      renderModal();
    }
  }, true);

  // General click interactions (widget open, modal close, remove, clear)
  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-open-compare-modal]')) {
      event.preventDefault();
      openModal();
      return;
    }

    if (event.target.closest('[data-close-compare-modal]')) {
      event.preventDefault();
      closeModal();
      return;
    }

    const removeButton = event.target.closest('[data-remove-compare]');
    if (removeButton) {
      event.preventDefault();
      const targetHandle = removeButton.dataset.removeCompare;
      const handles = getHandles().filter((h) => h !== targetHandle);
      saveHandles(handles);
      syncButtonsAndWidget(handles);
      renderModal();
      return;
    }

    if (event.target.closest('[data-clear-compare]')) {
      event.preventDefault();
      saveHandles([]);
      syncButtonsAndWidget([]);
      renderModal();
      return;
    }
  });

  // ESC key to close modal
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && modal && modal.classList.contains('is-open')) {
      closeModal();
    }
  });

  // Initial sync on page load
  syncButtonsAndWidget(getHandles());
})();
